import { style, styleVariants } from "@vanilla-extract/css";
import { primitiveTokens, semanticThemeTokens } from "../theme/ThemeTokens.css";

export const inputFieldBase = style({
  backgroundColor: semanticThemeTokens.input.backgroundColor,
  borderStyle: "solid",
  borderRadius: primitiveTokens.radii.sm,
  borderWidth: primitiveTokens.strokes.md,
  caretColor: semanticThemeTokens.input.cursorColor,
  overflow: "hidden",
  textOverflow: "ellipsis",
  transition: semanticThemeTokens.transition,
  whiteSpace: "nowrap",
  width: "100%",
  boxSizing: "border-box"
});

// The two steps of `selectTriggerSizes`, so an input sits flush with a select
// of the same size. `sm`'s padding is tighter — 12px would leave the 32px box
// no content area.
export const inputFieldSizes = styleVariants({
  md: {
    height: primitiveTokens.sizing["10x"],
    fontSize: primitiveTokens.type.sm,
    padding: `${primitiveTokens.sizing["3x"]} ${primitiveTokens.sizing["2x"]}`
  },
  sm: {
    height: primitiveTokens.sizing["8x"],
    fontSize: primitiveTokens.type.xs,
    padding: `${primitiveTokens.sizing["1x"]} ${primitiveTokens.sizing["2x"]}`
  }
});

export const inputFieldFilled = style({
  backgroundColor: semanticThemeTokens.input.backgroundColor,
  borderColor: semanticThemeTokens.input.filled.default.borderColor,
  color: semanticThemeTokens.input.filled.textColor,
  selectors: {
    "&:disabled": {
      backgroundColor:
        semanticThemeTokens.input.filled.disabled.backgroundColor,
      borderColor: semanticThemeTokens.input.filled.disabled.borderColor,
      color: semanticThemeTokens.input.filled.disabled.textColor
    },
    "&:not(:disabled):hover": {
      borderColor: semanticThemeTokens.input.filled.hover.borderColor
    },
    "&:not(:disabled):focus": {
      borderColor: semanticThemeTokens.input.filled.active.borderColor,
      boxShadow: primitiveTokens.colors.shadow.focusRing,
      outline: "none"
    },

    "&.inputError": {
      borderColor: semanticThemeTokens.input.invalid.borderColor,
      backgroundColor: semanticThemeTokens.input.invalid.backgroundColor
    },
    "&.inputError:hover": {
      borderColor: semanticThemeTokens.input.invalid.borderColor
    },
    "&.inputError:focus": {
      borderColor: semanticThemeTokens.input.invalid.borderColor,
      outline: "none",
      boxShadow: "none"
    }
  }
});

export const inputFieldUnfilled = style({
  borderColor: semanticThemeTokens.input.unfilled.default.borderColor,
  color: semanticThemeTokens.input.unfilled.textColor,
  selectors: {
    "&:disabled": {
      backgroundColor:
        semanticThemeTokens.input.unfilled.disabled.backgroundColor,
      borderColor: semanticThemeTokens.input.unfilled.disabled.borderColor,
      color: semanticThemeTokens.input.unfilled.disabled.textColor
    },
    "&:not(:disabled):focus": {
      borderColor: semanticThemeTokens.input.unfilled.active.borderColor,
      boxShadow: primitiveTokens.colors.shadow.focusRing,
      outline: "none"
    },
    "&:not(:disabled):hover": {
      borderColor: semanticThemeTokens.input.unfilled.hover.borderColor
    },

    "&.inputError": {
      borderColor: semanticThemeTokens.input.invalid.borderColor,
      backgroundColor: semanticThemeTokens.input.invalid.backgroundColor
    },
    "&.inputError:hover": {
      borderColor: semanticThemeTokens.input.invalid.borderColor
    },
    "&.inputError:focus": {
      borderColor: semanticThemeTokens.input.invalid.borderColor,
      outline: "none",
      boxShadow: "none"
    }
  }
});

// TODO: Refactor this into a reusable Box component
// Container for the input field, select, and its label

export const fieldContainer = style({
  display: "flex",
  flexDirection: "column",
  gap: primitiveTokens.sizing["1x"],
  minWidth: "200px",
  height: "auto"
});

export const numberFieldWrapper = style({
  position: "relative",
  width: "100%"
});

export const inputFieldNumber = style({
  // Stepper width (6x) plus breathing room so digits don't butt against it.
  paddingRight: primitiveTokens.sizing["8x"],
  // Native spin buttons are replaced by the stepper below.
  MozAppearance: "textfield",
  selectors: {
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0
    }
  }
});

export const stepperGroup = style({
  position: "absolute",
  top: primitiveTokens.strokes.md,
  right: primitiveTokens.strokes.md,
  bottom: primitiveTokens.strokes.md,
  display: "flex",
  flexDirection: "column",
  width: primitiveTokens.sizing["6x"],
  borderLeft: `${primitiveTokens.strokes.md} solid ${semanticThemeTokens.input.unfilled.default.borderColor}`,
  borderTopRightRadius: primitiveTokens.radii.xs,
  borderBottomRightRadius: primitiveTokens.radii.xs,
  overflow: "hidden",
  // Only shown (and usable) while hovering or focused — hidden otherwise.
  opacity: 0,
  pointerEvents: "none",
  transition: semanticThemeTokens.transition,
  selectors: {
    [`${numberFieldWrapper}:hover &`]: {
      opacity: 1,
      pointerEvents: "auto"
    },
    [`${numberFieldWrapper}:focus-within &`]: {
      opacity: 1,
      pointerEvents: "auto"
    }
  }
});

export const stepperButton = style({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  padding: 0,
  backgroundColor: "transparent",
  color: semanticThemeTokens.input.unfilled.textColor,
  cursor: "pointer",
  selectors: {
    "&:not(:first-child)": {
      borderTop: `${primitiveTokens.strokes.md} solid ${semanticThemeTokens.input.unfilled.default.borderColor}`
    },
    "&:hover:not(:disabled)": {
      backgroundColor: semanticThemeTokens.input.unfilled.hover.borderColor
    },
    "&:disabled": {
      cursor: "not-allowed",
      color: semanticThemeTokens.input.unfilled.disabled.textColor
    }
  }
});
